import React, { useState } from 'react';
import { Form, Input, Button, Typography, Alert, notification } from 'antd';
import { LockOutlined, UserOutlined, MailOutlined } from '@ant-design/icons';
import { NavLink, useNavigate } from 'react-router-dom';
import Captcha from 'components/common/Captcha';
import { storage } from 'helpers';
import authService from 'services/auth.services';
import LoginImage from 'assets/images/static/login_draw_managament.svg';
import roles from '../../helpers/roles';

const Login = () => {
  const [loginForm] = Form.useForm();
  const [otpForm] = Form.useForm();
  const [captchaForm] = Form.useForm();

  const [isLoading, setIsLoading] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);
  const [captchaLoading, setCaptchaLoading] = useState(false);

  const [error, setError] = useState(null);
  const [activeForm, setActiveForm] = useState('login'); // 'login' | 'otp' | 'captcha'
  const [captchaData, setCaptchaData] = useState(null);
  const [captchaChars, setCaptchaChars] = useState('');
  const [otpEmail, setOtpEmail] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const navigate = useNavigate();

  const handleLogin = async () => {
    try {
      const values = await loginForm.validateFields();
      setIsLoading(true);
      const loginResponse = await authService.login(values.email, values.password);
      setIsLoading(false);

      if (loginResponse.csrfToken) {
        setCsrfToken(loginResponse.csrfToken);
      }

      setOtpEmail(values.email);
      setActiveForm('otp');
      setError(null);
      otpForm.resetFields();
      setOtpValue('');
    } catch (err) {
      setIsLoading(false);
      if (err === '3001') {
        setActiveForm('captcha');
        const captchaResponse = await authService.getCaptcha({
          level: 1,
          soundRequested: false,
          length: 6,
        });
        setCaptchaData({
          image: captchaResponse?.payload?.message?.image,
          identifier: captchaResponse?.payload?.message?.identifier,
        });
        setError(null);
        captchaForm.resetFields();
        setCaptchaChars('');
        return;
      }
      if (err === '3002') {
        notification.error({
          message: 'Şifre Süresi Doldu',
          description: 'Şifrenizin süresi doldu, lütfen şifrenizi değiştirin.',
          duration: 3,
        });
        navigate('/forgot-password');
        return;
      }
      if (err.errorFields) {
        return;
      }
      setError('Giriş Başarısız! Lütfen bilgilerinizi kontrol edin.');
    }
  };

  const handleOtpVerify = async () => {
    try {
      setOtpLoading(true);

      const verifyResponse = await authService.verifyOtp(
        {
          email: otpEmail,
          otp: otpValue,
        },
        csrfToken,
      );

      const { token } = JSON.parse(verifyResponse.payload);

      const user = await authService.getCurrentUser(token);
      storage.setToken(token);
      storage.setUserInfo(user.payload);

      setOtpLoading(false);
      setError(null);
      if (user.payload?.role?.name === roles.PAYE_ADMIN) {
        navigate('/duyurular');
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      setOtpLoading(false);
      if (err === 'Otp Doğrulama Deneme Limiti Aşıldı') {
        setActiveForm('captcha');
        const captchaResponse = await authService.getCaptcha({
          level: 1,
          soundRequested: false,
          length: 6,
        });
        setCaptchaData({
          image: captchaResponse?.payload?.message?.image,
          identifier: captchaResponse?.payload?.message?.identifier,
        });
        setError(null);
        captchaForm.resetFields();
        setCaptchaChars('');
        return;
      }
      setError('OTP doğrulaması başarısız! Lütfen tekrar deneyin.');
    }
  };

  const handleCaptchaValidate = async () => {
    try {
      const values = await captchaForm.validateFields();
      setCaptchaLoading(true);
      const captchaResult = await authService.checkCaptcha({
        identifier: captchaData.identifier,
        captchaText: captchaChars,
      });
      setCaptchaLoading(false);
      if (captchaResult.isSuccess) {
        setActiveForm('login');
        setCaptchaChars('');
        setCaptchaData(null);
        setError(null);
        loginForm.resetFields();
      } else {
        setError('Captcha doğrulaması başarısız!');
      }
    } catch {
      setCaptchaLoading(false);
      setError('Captcha doğrulaması başarısız!');
    }
  };

  const renderLoginForm = () => (
    <Form form={loginForm} onFinish={handleLogin} layout="vertical">
      <Typography.Title level={4}>Paye Kart Yönetim Paneli</Typography.Title>
      <Form.Item name="email" label="E-posta" rules={[{ required: true, message: 'Zorunlu Alan' }]}>
        <Input
          className="input__default"
          prefix={<UserOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          placeholder="E-posta"
        />
      </Form.Item>
      <Form.Item
        name="password"
        label="Şifre"
        rules={[{ required: true, message: 'Zorunlu Alan' }]}
      >
        <Input
          className="input__default"
          prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          placeholder="Şifre"
          type="password"
        />
      </Form.Item>
      {error && (
        <Form.Item>
          <Alert message={error} type="error" showIcon />
        </Form.Item>
      )}
      <Form.Item>
        <NavLink to="/register">
          <Button
            className="button button__outline mr-3"
            type="primary"
            htmlType="button"
            disabled={isLoading}
            loading={isLoading}
          >
            KAYIT OL
          </Button>
        </NavLink>
        <Button
          className="button button__fill"
          type="primary"
          htmlType="submit"
          disabled={isLoading}
          loading={isLoading}
        >
          GİRİŞ
        </Button>
      </Form.Item>
      <Form.Item>
        <NavLink to="/forgot-password">Şifremi Unuttum</NavLink>
        <NavLink className="ml-4" to="/bize-ulasin">
          Bize Ulaşın
        </NavLink>
      </Form.Item>
    </Form>
  );

  const renderOtpForm = () => (
    <Form form={otpForm} onFinish={handleOtpVerify} layout="vertical">
      <Typography.Title level={4}>OTP Doğrulama</Typography.Title>
      <Form.Item>
        <Alert
          message={`E-posta adresinize gönderilen OTP kodunu giriniz: ${otpEmail}`}
          type="info"
          showIcon
        />
      </Form.Item>
      <Form.Item
        name="otp"
        label="OTP Kodu"
        rules={[{ required: true, message: 'OTP kodu zorunlu!' }]}
      >
        <Input
          className="input__default"
          prefix={<MailOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          placeholder="OTP Kodu"
          maxLength={6}
          value={otpValue}
          onChange={(e) => {
            setOtpValue(e.target.value);
            otpForm.setFieldsValue({ otp: e.target.value });
          }}
        />
      </Form.Item>
      {error && (
        <Form.Item>
          <Alert message={error} type="error" showIcon />
        </Form.Item>
      )}
      <Form.Item>
        <Button
          className="button button__fill"
          type="primary"
          htmlType="submit"
          disabled={otpLoading || otpValue.length < 6}
          loading={otpLoading}
        >
          OTP Doğrula
        </Button>
      </Form.Item>
    </Form>
  );

  const renderCaptchaForm = () => (
    <Form form={captchaForm} onFinish={handleCaptchaValidate} layout="vertical">
      <Typography.Title level={4}>Güvenlik Doğrulaması</Typography.Title>
      <Form.Item>
        <Captcha
          data={captchaData}
          captchaChars={captchaChars}
          onChangeValue={(e) => setCaptchaChars(e.target.value)}
          refreshCaptcha={async () => {
            const captchaResponse = await authService.getCaptcha({
              level: 1,
              soundRequested: false,
              length: 6,
            });
            setCaptchaData({
              image: captchaResponse?.payload?.message?.image,
              identifier: captchaResponse?.payload?.message?.identifier,
            });
            setCaptchaChars('');
            captchaForm.resetFields();
          }}
        />
      </Form.Item>
      {error && (
        <Form.Item>
          <Alert message={error} type="error" showIcon />
        </Form.Item>
      )}
      <Form.Item style={{ display: 'flex', justifyContent: 'center' }}>
        <Button
          className="button button__fill"
          type="primary"
          htmlType="submit"
          disabled={captchaLoading || captchaChars.length < 6}
          loading={captchaLoading}
        >
          Doğrula
        </Button>
      </Form.Item>
    </Form>
  );

  let content;
  if (activeForm === 'login') {
    content = renderLoginForm();
  } else if (activeForm === 'otp') {
    content = renderOtpForm();
  } else if (activeForm === 'captcha') {
    content = renderCaptchaForm();
  }

  return (
    <div className="login-page flex-row align-items-center">
      <div className="login-card">
        <div className="login-form-wrapper">
          <img style={{ width: '44%' }} src={LoginImage} alt="" />
          <h1 style={{ position: 'fixed', marginTop: '250px', marginRight: '275px' }}>
            ÜYE İŞYERİ
          </h1>
          {content}
        </div>
      </div>
    </div>
  );
};

export default Login;
